import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';

const VillaNajatScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const amenities = t('villas.najat.amenities', { returnObjects: true }) || [
    'Air conditioning throughout',
    'Traditional Moroccan salon with 2 extra bed option',
    'Spacious European-style living room with fireplace',
    'Fully equipped kitchen with gas stove and dishwasher',
    '2 bedrooms with private bathrooms (shower & bathtub)',
    'Additional separate bathroom with shower',
    'Beautiful large garden',
    'Heated pool (15 x 5 m)',
    'Sun loungers with umbrellas',
    'Lounge area',
    'Terrace with dining table',
    'Ping pong table',
    'Boccia court',
    'Parking space',
    'Bath and shower towels',
    'Washing machine and dryer',
    'WiFi throughout the villa',
    'Solar panels'
  ];

  const images = [
    '/assets/images/villa Baidane-15.jpg',
    '/assets/images/villa Baidane-16.jpg',
    '/assets/images/villa Baidane-17.jpg',
    '/assets/images/villa Baidane-18.jpg',
    '/assets/images/villa Baidane-19.jpg',
    '/assets/images/villa Baidane-20.jpg'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/assets/images/villa Baidane-15.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-sofia font-bold mb-4">
            {t('villas.najat.title')}
          </h1>
          <p className="text-xl font-sofia font-light">
            {t('villas.najat.subtitle')}
          </p>
          <div className="inline-block bg-[#81736d] text-white px-4 py-2 rounded-full text-sm font-sofia font-medium mt-3">
            {t('villas.najat.availableDate', 'Available from 1 October')}
          </div>
        </div>
      </section>

      {/* Villa Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-sofia font-bold text-black mb-6">
                {t('villas.najat.title')}
              </h2>
              <p className="text-md font-sofia text-[#81736d] mb-2">
                {t('villas.najat.pricing', 'Pricing: 500€ – 650€ per night')}
              </p>
              <p className="text-sm font-sofia text-text-light mb-6">
                {t('villas.najat.notePhotos', 'Note: Final photos of Villa Najat are coming soon. The villa is in the final stage of preparation.')}
              </p>
              <p className="text-lg font-sofia text-text-secondary mb-6">
                {t('villas.najat.description')}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">450m²</div>
                  <div className="font-sofia text-black">{t('gallery.stats.livingSpace')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">2</div>
                  <div className="font-sofia text-black">{t('villas.najat.bedrooms')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">15x5m</div>
                  <div className="font-sofia text-black">{t('villas.najat.pool')}</div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white">6</div>
                  <div className="font-sofia text-black">{t('villas.najat.capacity')}</div>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full md:w-auto"
                onClick={() => navigate('/booking')}
              >
                {t('villas.najat.bookNow')}
              </Button>
            </div>

            <div>
              <img 
                src="/assets/images/villa Baidane-16.jpg" 
                alt="Villa Najat interior"
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-6"
              />
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="/assets/images/villa Baidane-17.jpg" 
                  alt="Villa Najat pool"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <img 
                  src="/assets/images/villa Baidane-18.jpg" 
                  alt="Villa Najat bedroom"
                  className="w-full h-32 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-16 "  style={{ background: 'url("/assets/icons/top-img-bg.png") ' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-sofia font-bold text-black text-center mb-12">
            {t('villas.najat.amenitiesTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                <div className="w-6 h-6 bg-[#81736d] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="font-sofia text-text-primary">{amenity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-sofia font-bold text-black text-center mb-12">
            {t('villas.najat.galleryTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div key={index} className="relative group cursor-pointer">
                <img 
                  src={image} 
                  alt={`Villa Najat ${index + 1}`}
                  className="w-full h-64 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-300 rounded-lg"></div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => navigate('/gallery')}
            >
              {t('villas.najat.viewGallery')}
            </Button>
            <Button
              variant="outline"
              className="ml-4"
              onClick={() => navigate('/our-villas')}
            >
              {t('ourVillas.viewAllVillas')}
            </Button>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-15.jpg')`
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 "></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-sofia font-bold mb-4">
            {t('villas.najat.ctaTitle')}
          </h2>
          <p className="text-lg font-sofia font-light mb-8">
            {t('villas.najat.ctaDescription')}
          </p>
          <div className="space-x-4">
            <Button
              size="lg"
                className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => navigate('/booking')}
            >
              {t('villas.najat.checkAvailability')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-[#81736d] hover:text-white"
              onClick={() => navigate('/contact')}
            >
              {t('common.contactUs')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VillaNajatScreen;
